import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../configs";

const msal = new PublicClientApplication(msalConfig);

/**
 * Initializes MSAL and processes any pending redirect response.
 *
 * Works around a race condition in WebAppAuthHandler: the handler calls
 * loginRedirect() without awaiting it, then immediately calls
 * fetchAuthData() → acquireTokenSilent() → loginRedirect() again.
 * This double-fire corrupts the msal.interaction.status flag in
 * sessionStorage while leaving the PKCE data intact.
 *
 * We detect this state (hash present + PKCE cached + no interaction flag)
 * and restore the flag so handleRedirectPromise can process the response.
 */
export const initializeMsal = async () => {
  await msal.initialize();

  // Detect a stranded redirect: auth code hash is present and PKCE data
  // exists in sessionStorage, but the interaction-status flag is missing.
  if (typeof window !== "undefined" && typeof sessionStorage !== "undefined") {
    const clientId = msalConfig.auth?.clientId;
    if (!clientId) {
      console.warn("Client ID is undefined. Skipping redirect workaround.");
    } else {
      const hasCodeHash = window.location.hash.includes("code=");
      const interactionKey = "msal.interaction.status";
      const hasPkceData = !!sessionStorage.getItem(`msal.${clientId}.request.params`);
      const hasInteractionFlag = !!sessionStorage.getItem(interactionKey);

      if (hasCodeHash && hasPkceData && !hasInteractionFlag) {
        // Restore the missing interaction flag so handleRedirectPromise
        // will actually process the auth code in the URL hash.
        sessionStorage.setItem(interactionKey, JSON.stringify({ clientId, type: "redirect" }));
      }
    }
  }

  const response = await msal.handleRedirectPromise();
  if (response?.account) {
    msal.setActiveAccount(response.account);
  }
};

type Scopes = {
  diningApi: string;
};

export const scopes: Scopes = {
  diningApi: msalConfig.auth?.clientId + "/.default",
};

export default msal;
